import { CreditCard, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice, calculateVAT, calculateTotalTTC } from "@/utils/creditUtils";

/**
 * Composant OrderSummary - Récapitulatif de commande
 * 
 * Affiche le récapitulatif de l'achat avec :
 * - Nombre de crédits et prix HT
 * - TVA (20%)
 * - Total TTC
 * - Bouton de paiement
 */
interface OrderSummaryProps {
  credits: number | null;
  priceHT: number;
  onProceedToPayment: () => void;
  isProcessing: boolean;
  disabled: boolean;
}

export const OrderSummary = ({
  credits,
  priceHT,
  onProceedToPayment,
  isProcessing,
  disabled,
}: OrderSummaryProps) => {
  const vat = calculateVAT(priceHT);
  const totalTTC = calculateTotalTTC(priceHT);

  return (
    <div className="bg-white rounded-[20px] p-6 h-fit sticky top-5 shadow-sm">
      {/* Titre */}
      <div className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-5">
        Récapitulatif
      </div>

      {/* Lignes de détail */}
      <div className="space-y-4">
        {/* Crédits */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-100">
          <div className="text-sm text-gray-600">
            {credits || 0} Crédit{credits !== 1 ? 's' : ''}
          </div>
          <div className="text-sm text-[#1A212B] font-medium">
            {formatPrice(priceHT)}
          </div>
        </div>

        {/* TVA */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-100">
          <div className="text-sm text-gray-600">TVA (20%)</div>
          <div className="text-sm text-[#1A212B] font-medium">
            {formatPrice(vat)}
          </div>
        </div>

        {/* Total */}
        <div className="flex justify-between items-center pt-4 border-t-2 border-[#1A212B]">
          <div className="text-base text-[#1A212B] font-semibold">Total</div>
          <div className="text-[28px] text-[#1A212B] font-semibold tracking-[-1px]">
            {formatPrice(totalTTC)}
          </div>
        </div>
      </div>

      {/* Bouton de paiement */}
      <Button
        onClick={onProceedToPayment}
        disabled={disabled || isProcessing}
        className="w-full mt-5 bg-[#1A212B] hover:bg-[#2a3441] text-white rounded-[14px] py-6 text-[15px] font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg"
      >
        {isProcessing ? (
          <>
            <CreditCard className="h-4 w-4 mr-2 animate-pulse" />
            Redirection en cours...
          </>
        ) : (
          "Procéder au paiement"
        )}
      </Button>

      {/* Bannière info */}
      <div className="flex items-center gap-2 pt-4 mt-4 border-t border-gray-200">
        <Info className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
        <p className="text-xs text-slate-600 leading-relaxed">
          <strong className="font-semibold text-slate-700">1 crédit = 1 prise de référence</strong> (sans expiration)
        </p>
      </div>
    </div>
  );
};
